/**
 * STEP GROWTH-B3 · LIVE BOOST public landing page
 * URL: /gl/:campaignId
 *
 * Flow:
 *   LIVE BOOST URL
 *     → attribution.landing (anonymous)
 *     → (viewer_boost)  click → attribution.live_view → external/internal broadcast URL
 *     → (reminder_boost) Kakao login → attribution.login → /api/follow (existing REUSE)
 *                       → attribution.live_reminder (dedup on server)
 *
 * NO changes to /api/follow · NO changes to seller_notification_settings.
 * seller_notification_settings.broadcast_live_start remains READ-ONLY here —
 * enabling that switch is out of scope until BizM template approval.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertCircle, Bell, Radio, ExternalLink, Calendar } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const genSessionId = () => {
  try {
    const k = 'lb_sid';
    let sid = sessionStorage.getItem(k);
    if (!sid) {
      sid = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(k, sid);
    }
    return sid;
  } catch { return 'sess_' + Date.now(); }
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString('ko-KR') : '-');

export default function LiveBoostLanding({ user }) {
  const { campaignId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = useMemo(() => genSessionId(), []);

  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [campaign, setCampaign]   = useState(null);
  const [seller, setSeller]       = useState(null);
  const [broadcast, setBroadcast] = useState(null);
  const [status, setStatus]       = useState('idle'); // idle | processing | reminded | error
  const [isNewReminder, setIsNewReminder] = useState(null);

  // ── Step 1 · fetch campaign metadata + record landing
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!campaignId) {
        setError('유효하지 않은 캠페인 링크입니다.');
        setLoading(false);
        return;
      }
      try {
        const r = await api.get(`/growth/live-boost/${campaignId}/public`);
        if (!mounted) return;
        setCampaign(r.data);
        setSeller(r.data.seller);
        setBroadcast(r.data.broadcast);
      } catch (e) {
        setError(e?.response?.data?.detail || '캠페인 정보를 불러오지 못했습니다.');
        setLoading(false);
        return;
      }
      try {
        await api.post('/growth/live-boost/attribution/event', {
          campaign_id: campaignId,
          event_type:  'landing',
          session_id:  sessionId,
          source:      'liveboost_landing',
        });
      } catch { /* best-effort */ }
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [campaignId, sessionId]);

  // ── Step 2 (reminder_boost only) · after login, record login + follow + live_reminder
  useEffect(() => {
    if (!user || !campaign || status !== 'idle') return;
    if (campaign.live_boost_config?.promo_type !== 'reminder_boost') return;
    (async () => {
      setStatus('processing');
      try {
        await api.post('/growth/live-boost/attribution/event', {
          campaign_id: campaignId,
          event_type:  'login',
          session_id:  sessionId,
        });
      } catch { /* best-effort */ }
      try {
        await api.post(`/follow?seller_id=${encodeURIComponent(seller.seller_id)}`);
      } catch (e) {
        setStatus('error');
        return;
      }
      try {
        const r = await api.post('/growth/live-boost/attribution/event', {
          campaign_id: campaignId,
          event_type:  'live_reminder',
          session_id:  sessionId,
        });
        setIsNewReminder(!!r.data?.is_new_reminder);
      } catch { /* best-effort */ }
      setStatus('reminded');
    })();
  }, [user, campaign, campaignId, sessionId, seller, status]);

  const goLogin = () => {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    navigate(`/login?return_url=${returnUrl}`);
  };

  const onViewerClick = async () => {
    // Record live_view attribution then jump to the resolved destination.
    // STEP GROWTH-B3-FIX-2 · destination is resolved server-side:
    //   · live streaming session → /live-view/{session_id}
    //   · scheduled broadcast    → /live/{schedule_id} (waiting screen exists)
    //   · external URL           → the URL itself
    try {
      await api.post('/growth/live-boost/attribution/event', {
        campaign_id: campaignId,
        event_type:  'live_view',
        session_id:  sessionId,
      });
    } catch { /* best-effort */ }
    const url = campaign?.destination
      || campaign?.live_boost_config?.broadcast_url
      || (broadcast?.schedule_id ? `/live/${broadcast.schedule_id}` : null);
    if (!url) return;
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="liveboost-loading">
        <Loader2 size={22} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="liveboost-landing-error">
        <div className="max-w-md text-center">
          <AlertCircle size={40} className="mx-auto mb-3 text-[#FF3B30]" />
          <p className="font-bold">{error}</p>
        </div>
      </div>
    );
  }

  const lbc = campaign?.live_boost_config || {};
  // STEP GROWTH-B3-FIX-2 · V1 wizards create viewer_boost + creative_mode.
  // Legacy B3 reminder_boost drafts still route through the reminder branch
  // (kept intact for backward-compat).
  const isViewer = lbc.promo_type !== 'reminder_boost';
  const bcastLive = campaign?.is_live_now
    || String(broadcast?.status || '').toLowerCase() === 'live';

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10 bg-gradient-to-b from-white to-[#FFF3F1] dark:from-[#0A0A0A] dark:to-[#141414]">
      <div className="w-full max-w-md bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-2xl p-6 shadow-lg" data-testid="liveboost-card">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 rounded-full overflow-hidden bg-black/5 dark:bg-white/10 mb-4">
            <SafeImg src={seller?.profile_photo} kind="seller" alt={seller?.name} className="w-full h-full object-cover" />
          </div>
          <p className="text-xs uppercase tracking-widest text-[#FF3B30] font-black inline-flex items-center gap-1">
            {isViewer ? <Radio size={11} /> : <Bell size={11} />}
            {isViewer ? 'LIVE 시청자 유입 광고' : 'LIVE 알림 신청 광고'}
          </p>
          <h1 className="mt-1 text-2xl font-black" data-testid="liveboost-seller-name">{seller?.name || '판매자'}</h1>
          {lbc.promo_message && (
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line" data-testid="liveboost-promo-msg">{lbc.promo_message}</p>
          )}
          <p className="mt-3 text-xs text-gray-400">현재 단골 수 {(seller?.follower_count || 0).toLocaleString()}명</p>
        </div>

        {/* Broadcast card */}
        {broadcast && (
          <div className="mt-5 p-3 rounded-xl bg-black/5 dark:bg-white/5 flex gap-3 items-center" data-testid="liveboost-broadcast-card">
            <div className="w-16 h-16 rounded-lg bg-black/10 dark:bg-white/10 overflow-hidden flex-shrink-0">
              <SafeImg src={broadcast.thumbnail} kind="broadcast" alt={broadcast.title} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${bcastLive ? 'bg-red-500/10 text-red-600' : 'bg-black/5 dark:bg-white/10'}`}>
                  {bcastLive ? 'LIVE' : '예정'}
                </span>
                {broadcast.category && <span className="text-[10px] text-gray-400">{broadcast.category}</span>}
              </div>
              <p className="mt-1 text-sm font-bold truncate">{broadcast.title}</p>
              <p className="text-[11px] text-gray-500 inline-flex items-center gap-1"><Calendar size={10} /> {fmtDate(broadcast.scheduled_at)}</p>
            </div>
          </div>
        )}
        {!broadcast && lbc.broadcast_url && (
          <div className="mt-5 p-3 rounded-xl bg-black/5 dark:bg-white/5 text-center" data-testid="liveboost-external-hint">
            <p className="text-xs text-gray-500">외부 LIVE 방송으로 이동합니다.</p>
          </div>
        )}

        {/* CTA */}
        <div className="mt-6">
          {isViewer ? (
            <button
              onClick={onViewerClick}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#FF3B30] text-white font-black hover:brightness-95 transition"
              data-testid="liveboost-viewer-cta">
              <Radio size={16} /> {lbc.cta_label || '라이브 시청하기'}
            </button>
          ) : status === 'reminded' ? (
            <div className="rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 p-4 flex items-center gap-3" data-testid="liveboost-reminded">
              <CheckCircle2 size={22} />
              <div>
                <p className="font-bold text-sm">LIVE 알림이 신청되었습니다!</p>
                <p className="text-xs opacity-80">
                  {isNewReminder === true ? '단골 등록과 함께 다음 LIVE 알림을 받으실 수 있습니다.' :
                   isNewReminder === false ? '이미 이 판매자의 단골이었습니다.' :
                   '알림 신청이 처리되었습니다.'}
                </p>
              </div>
            </div>
          ) : status === 'error' ? (
            <div className="rounded-xl bg-red-500/10 text-red-600 p-4" data-testid="liveboost-error">
              <p className="font-bold text-sm">알림 신청 중 오류가 발생했어요. 다시 시도해주세요.</p>
              <button onClick={() => setStatus('idle')} className="mt-2 text-xs font-bold underline">다시 시도</button>
            </div>
          ) : !user ? (
            <button
              onClick={goLogin}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#FEE500] text-[#191919] font-black hover:brightness-95 transition"
              data-testid="liveboost-kakao-login">
              <Bell size={16} /> {lbc.cta_label || 'LIVE 알림 신청'}
            </button>
          ) : (
            <button
              disabled
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-gray-200 dark:bg-white/10 text-gray-500 font-black"
              data-testid="liveboost-processing">
              <Loader2 size={16} className="animate-spin" /> 알림 신청 중…
            </button>
          )}
        </div>

        <p className="mt-4 text-[11px] text-gray-400 text-center">
          {isViewer
            ? 'LIVE 방송 페이지로 이동합니다. 광고 성과 측정을 위해 이동 이력이 기록됩니다.'
            : '카카오 계정으로 로그인하면 자동으로 이 판매자의 단골이 되고 LIVE 알림 대상에 포함됩니다.'}
        </p>
      </div>
    </div>
  );
}
